import {
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  VStack,
  Link,
  List,
  ListItem,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useIsUserInRole } from '@transport-insights/uikit';
import { useReports } from '@src/modules/regulatory-reporting/context/reports';
import InfoPane from '@src/components/shared/InfoPane';
import { FiInfo } from 'react-icons/fi';
import { Redirect } from 'react-router-dom';
import ReportContainer from '../../components/ReportContainer';

function DataTable({ tableData, definitions, showTableNumber }) {
  const typeKeys = [...new Set(tableData.data.map((x) => x.data.map(({ type }) => type)).flat())];

  let roadType;

  if (tableData.description.includes('All Roads')) {
    roadType = 'All Roads';
  } else if (tableData.description.includes('Rural Roads')) {
    roadType = 'Rural Roads';
  } else if (tableData.description.includes('Urban Roads')) {
    roadType = 'Urban Roads';
  }

  return (
    <Box
      background="white"
      borderRadius="base"
      boxShadow="sm"
      mb={4}
      overflow="hidden"
    >
      {showTableNumber && (
      <Heading size="sm" p={4}>
        Table
        {' '}
        {tableData.number}
        {'. '}
        {tableData.description}
      </Heading>
      )}
      {!showTableNumber && (
      <VStack align="start" p={4} spacing={0}>
        {roadType && (
        <Text fontSize="xl" fontWeight="bold">
          {roadType}
        </Text>
        )}
        <Heading size="sm" fontWeight="normal">
          {tableData.description}
        </Heading>
      </VStack>
      )}
      <Table size="sm" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <Thead borderTop="1px solid" borderColor="gray.100">
          <Tr>
            <Th>&nbsp;</Th>
            {typeKeys.map((type) => (
              <Th key={type} textAlign="right">
                {type}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {tableData.data.map((category) => {
            const isLastRow = tableData.data[tableData.data.length - 1].category === category.category;
            return (
              <Tr
                key={category.category}
                fontWeight={isLastRow ? 'bold' : 'normal'}
                borderTop={isLastRow ? '2px solid' : 'inherit'}
                borderColor={isLastRow ? 'gray.300' : 'inherit'}
              >
                <Td>{category.category}</Td>
                {typeKeys.map((type) => {
                  const typeData = category.data.find((x) => x.type === type);
                  const isPercentage = typeData?.value?.includes('%');
                  return (
                    <Td
                      key={type}
                      textAlign="right"
                    >
                      {typeData && (
                        <>
                          <Box
                            fontWeight="inherit"
                            fontSize={isPercentage && isLastRow ? '2xl' : 'inherit'}
                            color={isPercentage && isLastRow ? 'brand.orange.500' : 'inherit'}
                          >
                            {typeData.helpRef && (
                              <Popover>
                                <PopoverTrigger>
                                  <IconButton
                                    aria-label="Help"
                                    icon={<Icon as={FiInfo} boxSize={4} />}
                                    size="sm"
                                    variant="ghost"
                                    color="gray.400"
                                    _hover={{ color: 'gray.600' }}
                                    mr={0}
                                  />
                                </PopoverTrigger>
                                <PopoverContent _focus={{ boxShadow: 'md' }}>
                                  <PopoverArrow />
                                  <PopoverBody textAlign="left">
                                    <Text
                                      as="span"
                                      fontSize="sm"
                                      color="gray.500"
                                      textAlign="left"
                                      fontWeight="normal"
                                    >
                                      {definitions.find((x) => x.helpRef === typeData.helpRef)?.definition}
                                    </Text>
                                  </PopoverBody>
                                </PopoverContent>
                              </Popover>
                            )}
                            {typeData.value}
                          </Box>
                          {typeData.additionalText && (
                          <Text fontSize="xs" color="gray.500" fontWeight="normal">
                            {typeData.additionalText}
                          </Text>
                          )}
                        </>
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

export default function SmoothTravelExposureLtpTarget() {
  const { data: report, isLoading: isLoadingReport, error } = useReports('ste-target');
  const percentageTables = report?.tables?.filter((table) => table.number > 6).reverse();
  const isDeveloper = useIsUserInRole('Developer');

  if (!isDeveloper && !report) {
    return <Redirect to="/regulatory-reporting/reports" />;
  }

  return (
    <ReportContainer
      title="Smooth Travel Exposure Target setting for 2027-37 LTP"
      isLoading={isLoadingReport}
      noData={isEmpty(report?.tables)}
      moreInfo={(
        <>
          <Text mb={4}>
            STE is a key performance measure used to assess the proportion of travel occurring on roads with acceptable smoothness. The measure relies on two key datasets: road condition (roughness) and traffic estimates.
          </Text>
          <Text mb={4}>
            Network length and traffic estimate data has been taken from the Carriageway (carr_way) table in AWM.
          </Text>
          <Text mb={4}>
            Condition (Roughness) data has been extracted from the 2024/25 and 2025/26 CCDC surveys provided to 10m lengths.
          </Text>
          <Text mb={4}>
            Each 10m roughness reading is allocated to the carriageway section/s that the reading overlaps, based on the linear start and end positions of the roughness records and carriageway sections.
          </Text>
          <Text mb={4}>
            Transport Insights provides a Traffic Count Estimation (TCE) module that calculates traffic estimates across an RCA’s road network. TCE calculates new Average Daily Traffic (ADT) estimates and traffic loading (a.k.a. traffic mix) for all locally owned road sections in your network using the traffic counts from the last six years and the current estimates.
          </Text>
          <Text mb={4}>
            The reported figures are rounded and therefore some totals may differ slightly because of this.
          </Text>
          <Heading as="h3" size="sm" mb={2}>
            Useful Links
          </Heading>
          <List spacing={2} mb={4}>
            <ListItem>
              <Link href="/Home/HelpLink/RegulatorySTE" isExternal color="blue.500">
                Smooth Travel Exposure Help
              </Link>
            </ListItem>
            <ListItem>
              <Link href="https://www.nzta.govt.nz/partners/planning-and-investment/planning/road-efficiency-group/consistent-condition-data-collection" isExternal color="blue.500">
                Consistent Condition Data Collection
              </Link>
            </ListItem>
            <ListItem>
              Smooth Travel Exposure (based on 10m roughness data) update August 2026 (To be developed)
            </ListItem>
          </List>
        </>
      )}
      error={error}
    >
      <InfoPane mt={0}>
        <Text>
          Smooth Travel Exposure (STE) is a mandatory non-financial performance measure reported on by councils against targets set during consultation with communities in the development of their Long Term Plans.
        </Text>
        <Text>
          In preparation for setting targets for the 2027-2037 Long Term Plans, the following indicative results have been calculated using CCDC roughness data reported in 10m intervals (rather than the historical 100m records).
        </Text>
      </InfoPane>
      {percentageTables?.map((table) => (
        <DataTable
          key={table.number}
          tableData={table}
          definitions={report.definitions}
          showTableNumber={false}
        />
      ))}
      <Text>
        Note: This data has been extracted from all Carriageway Sections owned by Local Authority and excludes the following event codes: detour route/temporary alignment/change from nominated lane (D),) works (Road works, marking, etc) (W), surface contamination (J), wet road (N), sensor drop outs exceed 5% of the number of samples for each reporting interval (C), unsealed road (U), speed outside limits (below 5pkh or where excessive changes in acceleration are identified (S), discrepancy in length/locational referencing (L), or data invalid for any other reasons (Z).
      </Text>
    </ReportContainer>
  );
}
